import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import SEO, { type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import ContentIndex from '@/components/ContentIndex';
import CTABand from '@/components/CTABand';
import { contentBySection, staticPage } from '@/lib/content';

const SECTORS = [
  { name: 'Healthcare', to: '/sectors/healthcare', body: 'Patient and supplier records, retention rules, and imaging tied to the ERP.' },
  { name: 'Higher education', to: '/sectors/higher-education', body: 'Student and finance content, PeopleSoft and EBS integrations, long-lived repositories.' },
  { name: 'K-12', to: '/sectors/k12', body: 'District finance and HR content with small teams and fixed budget cycles.' },
  { name: 'Government and public sector', to: '/sectors/government-public-sector', body: 'Records management, retention schedules and multi-agency repositories at scale.' },
];

const Sectors = () => {
  const page = staticPage('/sectors');
  const items = contentBySection('sectors');
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Sectors', href: '/sectors' },
  ];
  return (
    <>
      <SEO title={page.title} description={page.description} path="/sectors" breadcrumbs={crumbs} />
      <PageHeader
        crumbs={crumbs}
        label="Sectors"
        title="WebCenter, sector by sector"
        lead="The product is the same; the estate is not. Integrations, records rules and upgrade windows differ by sector, and so does the order of work."
      />

      <section aria-labelledby="sectors-heading" className="container py-14 md:py-16">
        <h2 id="sectors-heading" className="sr-only">
          Sectors covered
        </h2>
        <ul className="grid gap-px border border-rule bg-rule sm:grid-cols-2">
          {SECTORS.map((s) => (
            <li key={s.name} className="bg-cream">
              <Link to={s.to} className="group block h-full p-7 hover:bg-cream-light focus-visible:outline-offset-[-4px]">
                <h3 className="flex items-start justify-between gap-4 text-[1.375rem] font-bold leading-[1.25]">
                  <span className="group-hover:underline group-hover:underline-offset-4">{s.name}</span>
                  <ArrowUpRight className="shrink-0" size={22} aria-hidden="true" />
                </h3>
                <p className="mt-2 text-body">{s.body}</p>
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-12">
          <ContentIndex
            items={items}
            emptyHeading="Sector guides are being added."
            emptyBody="Healthcare, higher education, K-12 and government pages are being written now."
          />
        </div>
      </section>

      <CTABand variant="engineer" />
    </>
  );
};

export default Sectors;
